import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  XMarkIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  TagIcon,
  CalendarDaysIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';

interface Category {
  id: string;
  tournament_id: string;
  name: string;
  description: string | null;
  sort_order: number;
}

interface Round {
  id: string;
  tournament_id: string;
  round_number: number;
  name: string;
  date: string | null;
  notes: string | null;
  sort_order: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tournamentId: string;
  tournamentName: string;
}

type ActiveSection = 'categories' | 'rounds';

function CategoryForm({
  initial,
  onSave,
  onCancel,
}: {
  initial?: Partial<Category>;
  onSave: (data: { name: string; description: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    await onSave({ name: name.trim(), description: description.trim() });
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-4 border border-gray-200 space-y-3 mx-4">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Category Name *</label>
        <input
          autoFocus
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Gross, Net, Ladies, Senior"
          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-golf-orange bg-white"
          required
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
        <input
          type="text"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Optional description"
          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-golf-orange bg-white"
        />
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 rounded-xl hover:bg-gray-100">Cancel</button>
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="flex items-center gap-1.5 px-4 py-2 text-sm bg-golf-orange text-white rounded-xl disabled:opacity-50"
        >
          <CheckIcon className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

function RoundForm({
  initial,
  nextRoundNumber,
  onSave,
  onCancel,
}: {
  initial?: Partial<Round>;
  nextRoundNumber: number;
  onSave: (data: { round_number: number; name: string; date: string; notes: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [roundNumber, setRoundNumber] = useState(initial?.round_number ?? nextRoundNumber);
  const [name, setName] = useState(initial?.name ?? `Round ${initial?.round_number ?? nextRoundNumber}`);
  const [date, setDate] = useState(initial?.date ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    await onSave({ round_number: roundNumber, name: name.trim(), date, notes: notes.trim() });
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-4 border border-gray-200 space-y-3 mx-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Round # *</label>
          <input
            type="number"
            min={1}
            value={roundNumber}
            onChange={e => {
              const n = parseInt(e.target.value);
              setRoundNumber(n);
              if (!initial?.name) setName(`Round ${n}`);
            }}
            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-golf-orange bg-white"
            required
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
          <input
            autoFocus
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Round 1"
            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-golf-orange bg-white"
            required
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-golf-orange bg-white"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
        <input
          type="text"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Optional notes"
          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-golf-orange bg-white"
        />
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 rounded-xl hover:bg-gray-100">Cancel</button>
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-500 text-white rounded-xl disabled:opacity-50"
        >
          <CheckIcon className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

export default function MobileTournamentCategoriesRounds({ isOpen, onClose, tournamentId, tournamentName }: Props) {
  const [activeSection, setActiveSection] = useState<ActiveSection>('categories');
  const [categories, setCategories] = useState<Category[]>([]);
  const [rounds, setRounds] = useState<Round[]>([]);
  const [disabledRounds, setDisabledRounds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [addingCategory, setAddingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null);
  const [addingRound, setAddingRound] = useState(false);
  const [editingRound, setEditingRound] = useState<Round | null>(null);
  const [deletingRound, setDeletingRound] = useState<Round | null>(null);

  useEffect(() => {
    if (isOpen && tournamentId) fetchAll();
  }, [isOpen, tournamentId]);

  const fetchAll = async () => {
    setLoading(true);
    const [catRes, roundRes, disabledRes] = await Promise.all([
      supabase.from('tournament_categories').select('*').eq('tournament_id', tournamentId).order('sort_order').order('created_at'),
      supabase.from('tournament_rounds').select('*').eq('tournament_id', tournamentId).order('sort_order').order('round_number'),
      supabase.from('system_settings').select('value').eq('key', `disabled_rounds_${tournamentId}`).maybeSingle(),
    ]);
    if (!catRes.error) setCategories(catRes.data ?? []);
    if (!roundRes.error) setRounds(roundRes.data ?? []);
    if (disabledRes.data?.value) {
      const arr = disabledRes.data.value as number[];
      setDisabledRounds(new Set(Array.isArray(arr) ? arr : []));
    } else {
      setDisabledRounds(new Set());
    }
    setLoading(false);
  };

  const saveCategory = async (data: { name: string; description: string }) => {
    if (editingCategory) {
      const { error } = await supabase.from('tournament_categories').update({ name: data.name, description: data.description || null }).eq('id', editingCategory.id);
      if (error) { toast.error('Failed to update category'); return; }
      toast.success('Category updated');
      setEditingCategory(null);
    } else {
      const { error } = await supabase.from('tournament_categories').insert({ tournament_id: tournamentId, name: data.name, description: data.description || null, sort_order: categories.length });
      if (error) { toast.error('Failed to add category'); return; }
      toast.success('Category added');
      setAddingCategory(false);
    }
    fetchAll();
  };

  const deleteCategory = async () => {
    if (!deletingCategory) return;
    const { error } = await supabase.from('tournament_categories').delete().eq('id', deletingCategory.id);
    if (error) { toast.error('Failed to delete'); return; }
    toast.success('Category deleted');
    setDeletingCategory(null);
    fetchAll();
  };

  const moveCat = async (cat: Category, dir: -1 | 1) => {
    const sorted = [...categories].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex(c => c.id === cat.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const swap = sorted[swapIdx];
    await Promise.all([
      supabase.from('tournament_categories').update({ sort_order: swap.sort_order }).eq('id', cat.id),
      supabase.from('tournament_categories').update({ sort_order: cat.sort_order }).eq('id', swap.id),
    ]);
    fetchAll();
  };

  const saveRound = async (data: { round_number: number; name: string; date: string; notes: string }) => {
    const payload = { round_number: data.round_number, name: data.name, date: data.date || null, notes: data.notes || null };
    if (editingRound) {
      const { error } = await supabase.from('tournament_rounds').update(payload).eq('id', editingRound.id);
      if (error) { toast.error('Failed to update round'); return; }
      toast.success('Round updated');
      setEditingRound(null);
    } else {
      const { error } = await supabase.from('tournament_rounds').insert({ tournament_id: tournamentId, ...payload, sort_order: rounds.length });
      if (error) { toast.error('Failed to add round'); return; }
      toast.success('Round added');
      setAddingRound(false);
    }
    fetchAll();
  };

  const deleteRound = async () => {
    if (!deletingRound) return;
    await supabase.from('hole_scores').delete().eq('round_id', deletingRound.id);
    await supabase.from('scorer_assignments').delete().eq('round_id', deletingRound.id);
    await supabase.from('player_pairings').delete().eq('round_id', deletingRound.id);
    const { error } = await supabase.from('tournament_rounds').delete().eq('id', deletingRound.id);
    if (error) { toast.error('Failed to delete'); return; }
    toast.success('Round deleted');
    setDeletingRound(null);
    fetchAll();
  };

  const moveRound = async (round: Round, dir: -1 | 1) => {
    const sorted = [...rounds].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex(r => r.id === round.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const swap = sorted[swapIdx];
    await Promise.all([
      supabase.from('tournament_rounds').update({ sort_order: swap.sort_order }).eq('id', round.id),
      supabase.from('tournament_rounds').update({ sort_order: round.sort_order }).eq('id', swap.id),
    ]);
    fetchAll();
  };

  const toggleRoundEnabled = async (round: Round) => {
    const roundNum = round.round_number;
    const newDisabled = new Set(disabledRounds);
    if (newDisabled.has(roundNum)) {
      newDisabled.delete(roundNum);
    } else {
      newDisabled.add(roundNum);
    }
    const arr = Array.from(newDisabled);
    const { error } = await supabase
      .from('system_settings')
      .upsert(
        { key: `disabled_rounds_${tournamentId}`, value: arr, category: 'tournament', is_public: true },
        { onConflict: 'key' }
      );
    if (error) {
      toast.error('Failed to update round');
      return;
    }
    setDisabledRounds(newDisabled);
    toast.success(newDisabled.has(roundNum) ? 'Round disabled' : 'Round enabled');
  };

  const nextRoundNumber = rounds.length > 0 ? Math.max(...rounds.map(r => r.round_number)) + 1 : 1;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/40 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-3xl max-h-[90vh] flex flex-col"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          >
            {/* Handle */}
            <div className="flex-shrink-0 flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div>
                <h2 className="font-bold text-golf-gray text-base">Categories & Rounds</h2>
                <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[220px]">{tournamentName}</p>
              </div>
              <button onClick={onClose} className="p-2 rounded-xl text-gray-400 hover:bg-gray-100">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Section toggle */}
            <div className="flex-shrink-0 flex gap-1 px-4 py-3">
              <button
                onClick={() => setActiveSection('categories')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  activeSection === 'categories' ? 'bg-golf-orange text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                <TagIcon className="w-4 h-4" />
                Categories ({categories.length})
              </button>
              <button
                onClick={() => setActiveSection('rounds')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  activeSection === 'rounds' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                <CalendarDaysIcon className="w-4 h-4" />
                Rounds ({rounds.length})
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto pb-8">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
                </div>
              ) : activeSection === 'categories' ? (
                <div className="space-y-2">
                  {/* Add button */}
                  {!addingCategory && !editingCategory && (
                    <div className="px-4">
                      <button
                        onClick={() => setAddingCategory(true)}
                        className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-golf-orange/30 rounded-2xl text-sm font-medium text-golf-orange hover:bg-golf-orange/5 transition-colors"
                      >
                        <PlusIcon className="w-4 h-4" />
                        Add Category
                      </button>
                    </div>
                  )}

                  <AnimatePresence>
                    {addingCategory && (
                      <motion.div key="add-cat" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                        <CategoryForm onSave={saveCategory} onCancel={() => setAddingCategory(false)} />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {categories.length === 0 && !addingCategory && (
                    <div className="px-4 text-center py-12">
                      <TagIcon className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                      <p className="text-sm text-gray-400">No categories yet</p>
                    </div>
                  )}

                  <div className="px-4 space-y-2">
                    {categories.map((cat, idx) => (
                      <div key={cat.id}>
                        {editingCategory?.id === cat.id ? (
                          <CategoryForm initial={cat} onSave={saveCategory} onCancel={() => setEditingCategory(null)} />
                        ) : (
                          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
                            <div className="flex flex-col gap-0.5 flex-shrink-0">
                              <button onClick={() => moveCat(cat, -1)} disabled={idx === 0} className="p-0.5 text-gray-300 disabled:opacity-20">
                                <ChevronUpIcon className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => moveCat(cat, 1)} disabled={idx === categories.length - 1} className="p-0.5 text-gray-300 disabled:opacity-20">
                                <ChevronDownIcon className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-golf-gray text-sm">{cat.name}</p>
                              {cat.description && <p className="text-xs text-gray-400 truncate">{cat.description}</p>}
                            </div>
                            <div className="flex items-center gap-1">
                              <button onClick={() => { setEditingCategory(cat); setAddingCategory(false); }} className="p-2 rounded-xl text-gray-400 hover:text-golf-orange hover:bg-golf-orange/5">
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button onClick={() => setDeletingCategory(cat)} className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50">
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Add button */}
                  {!addingRound && !editingRound && (
                    <div className="px-4">
                      <button
                        onClick={() => setAddingRound(true)}
                        className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-blue-300 rounded-2xl text-sm font-medium text-blue-500 hover:bg-blue-50 transition-colors"
                      >
                        <PlusIcon className="w-4 h-4" />
                        Add Round
                      </button>
                    </div>
                  )}

                  <AnimatePresence>
                    {addingRound && (
                      <motion.div key="add-round" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                        <RoundForm nextRoundNumber={nextRoundNumber} onSave={saveRound} onCancel={() => setAddingRound(false)} />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {rounds.length === 0 && !addingRound && (
                    <div className="px-4 text-center py-12">
                      <CalendarDaysIcon className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                      <p className="text-sm text-gray-400">No rounds defined yet</p>
                    </div>
                  )}

                  <div className="px-4 space-y-2">
                    {rounds.map((round, idx) => (
                      <div key={round.id}>
                        {editingRound?.id === round.id ? (
                          <RoundForm initial={round} nextRoundNumber={nextRoundNumber} onSave={saveRound} onCancel={() => setEditingRound(null)} />
                        ) : (
                          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
                            <div className="flex flex-col gap-0.5 flex-shrink-0">
                              <button onClick={() => moveRound(round, -1)} disabled={idx === 0} className="p-0.5 text-gray-300 disabled:opacity-20">
                                <ChevronUpIcon className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => moveRound(round, 1)} disabled={idx === rounds.length - 1} className="p-0.5 text-gray-300 disabled:opacity-20">
                                <ChevronDownIcon className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-blue-600">{round.round_number}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-golf-gray text-sm">{round.name}</p>
                                {disabledRounds.has(round.round_number) && (
                                  <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Off</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                {round.date && (
                                  <span className="text-xs text-gray-400">
                                    {new Date(round.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </span>
                                )}
                                {round.notes && <span className="text-xs text-gray-400 truncate">{round.notes}</span>}
                              </div>
                            </div>
                            <button
                              onClick={() => toggleRoundEnabled(round)}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${
                                !disabledRounds.has(round.round_number) ? 'bg-golf-green' : 'bg-gray-300'
                              }`}
                            >
                              <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transform transition-transform ${
                                !disabledRounds.has(round.round_number) ? 'translate-x-4.5' : 'translate-x-0.5'
                              }`} />
                            </button>
                            <div className="flex items-center gap-1">
                              <button onClick={() => { setEditingRound(round); setAddingRound(false); }} className="p-2 rounded-xl text-gray-400 hover:text-golf-orange hover:bg-golf-orange/5">
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button onClick={() => setDeletingRound(round)} className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50">
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}

      <DeleteConfirmationModal
        isOpen={!!deletingCategory}
        onClose={() => setDeletingCategory(null)}
        onConfirm={deleteCategory}
        title="Delete Category"
        message={`Delete "${deletingCategory?.name}"? This cannot be undone.`}
      />

      <DeleteConfirmationModal
        isOpen={!!deletingRound}
        onClose={() => setDeletingRound(null)}
        onConfirm={deleteRound}
        title="Delete Round"
        message={`Delete "${deletingRound?.name}"? This cannot be undone.`}
      />
    </AnimatePresence>
  );
}
